import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

/** Calendar date format (`yyyy-MM-dd`) used for practice-local days and Mantine date pickers. */
export const PRACTICE_CALENDAR_DATE_FORMAT = "yyyy-MM-dd";

/**
 * Returns the calendar date of an instant as seen on a wall clock in the practice timezone.
 *
 * @param instant Point in time to read.
 * @param timeZone IANA timezone of the practice.
 * @returns Calendar date as `yyyy-MM-dd`.
 */
export function getPracticeCalendarDate(instant: Date, timeZone: string): string {
  return formatInTimeZone(instant, timeZone, PRACTICE_CALENDAR_DATE_FORMAT);
}

/**
 * Moves a calendar date by a whole number of days. Works on the date alone, so daylight-saving
 * shifts in any timezone do not change the result.
 *
 * @param calendarDate Calendar date as `yyyy-MM-dd`.
 * @param days Days to add; negative values move earlier.
 * @returns Calendar date as `yyyy-MM-dd`.
 */
export function addCalendarDays(calendarDate: string, days: number): string {
  const date = new Date(`${calendarDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, PRACTICE_CALENDAR_DATE_FORMAT.length);
}

/**
 * Returns the first instant of a calendar date in the practice timezone.
 *
 * @param calendarDate Calendar date as `yyyy-MM-dd`.
 * @param timeZone IANA timezone of the practice.
 * @returns Practice-local midnight as an instant.
 */
export function getStartOfPracticeCalendarDate(calendarDate: string, timeZone: string): Date {
  return fromZonedTime(`${calendarDate}T00:00:00.000`, timeZone);
}

/**
 * Returns the last millisecond of a calendar date in the practice timezone.
 *
 * @param calendarDate Calendar date as `yyyy-MM-dd`.
 * @param timeZone IANA timezone of the practice.
 * @returns Practice-local 23:59:59.999 as an instant.
 */
export function getEndOfPracticeCalendarDate(calendarDate: string, timeZone: string): Date {
  return fromZonedTime(`${calendarDate}T23:59:59.999`, timeZone);
}
